import { DecimalPipe, PercentPipe, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { toCanvas } from 'html-to-image';
import { Subscription, firstValueFrom } from 'rxjs';

import { XIColorUsage } from '@inmoment/the-kitchen';

import { Constants } from 'app/constants/constants';
import { FilterService } from 'app/modules/collection/services/filter.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { ChartReportService } from 'app/modules/widget/services/chart-report.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';
import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { StringBuilder } from 'app/utils/string-builder';
import { CpppCalculationService } from './../../services/cppp-calculation.service';

import { WidgetDataSource } from '../../../../models';
import { HumanizeNumberPipe } from '../../../../pipes';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetChartOptions } from '../widget-chart/widget-chart.component';

import { RatingScoring } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-property-validator';
import { ChartWidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-widget-ai-insights-validator.service';
import { WidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';
import { IsValidNumberPipe } from './is-valid-number.pipe';

export interface BigNumberWidgetOptions extends WidgetChartOptions {
  customField?: any;
  metadataDisplayNameOne: string;
  analyticItemTwo?: string;
  metadataDisplayNameTwo?: string;
  metadataTwoCustomFieldSelected?: boolean;
  scoringStrategyTwo: string;
  boxDirectionTwo: string;
  boxSizeTwo: string;
  scoreRangeTwo: string;
  increaseColor: string;
  increaseColorTwo: string;
  decreaseColor: string;
  decreaseColorTwo: string;
  intPrecisionOne: string;
  intPrecisionTwo: string;
  showDisplayName: boolean;
  showDisplayNameTwo: boolean;
  displayDataAs: 'absolute' | 'percentage';
  displayDataAsTwo: 'absolute' | 'percentage';
}

interface BigNumberDataDisplay {
  analyticItem: string;
  analyticItemDisplayName: string;
  theBigNumber?: number;
  theSmallNumber?: number;
  vsTotalNumber?: number;
  vsPreviousNumber?: number;
  isVsTotalNumberLoading: boolean;
  isVsPreviousNumberLoading: boolean;
  isPreviousPeriod0?: boolean;
  increaseArrowColor?: { color: string; id: string };
  decreaseArrowColor?: { color: string; id: string };
  showVsTotal?: boolean;
  showDisplayName: boolean;
  displayDataAs: 'absolute' | 'percentage';
  previousAnalyticItem: string;
  unfilteredValue?: number;
  intPrecision: string;
  reportOptions: { [key: string]: any };
  theBigNumberDocumentCount: number;
  theSmallNumberDocumentCount: number;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-big-number',
  templateUrl: './widget-big-number.component.html',
  styleUrls: ['./widget-big-number.component.scss'],
  providers: [
    { provide: WidgetAiInsightsValidatorService, useClass: ChartWidgetAiInsightsValidatorService },
    DecimalPipe,
    PercentPipe,
    TitleCasePipe,
    IsValidNumberPipe,
    HumanizeNumberPipe,
  ],
  standalone: false,
})
export class WidgetBigNumberComponent
  extends AbstractWidgetComponent<BigNumberWidgetOptions>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('widgetContainer', { read: ElementRef }) widgetContainer;

  multipleFields: boolean;
  fieldOne: BigNumberDataDisplay = {} as BigNumberDataDisplay;
  fieldTwo: BigNumberDataDisplay = {} as BigNumberDataDisplay;
  bigNumberArrowColors = [
    { color: 'neutral', id: 'gray' },
    { color: 'negative', id: 'red' },
    { color: 'positive', id: 'green' },
  ];

  canFilter = true;
  hasAnalyses = true;

  groupFieldsSub$: Subscription;
  accountNumericCustomFields: any[];
  isAnalyticItemEnabled;
  isImageExporting;
  widgetQueryOptions: any;
  comparisonFilterCount: number;
  private availableFields: AccountMetadata;
  minHeight = 2;
  comparisonBucketNames: string;
  displayEmptyState = false;
  insightPointSelectionState = {
    active: false,
    minPoints: 0,
    maxPoints: 0,
    count: 0,
    isApplyComparison: false,
    fieldOne: {
      disabled: false,
      theBigNumber: {
        selected: false,
        index: -1,
      },
      theSmallNumber: {
        selected: false,
        disabled: false,
        index: -1,
      },
    },
    fieldTwo: {
      disabled: false,
      theBigNumber: {
        selected: false,
        index: -1,
      },
      theSmallNumber: {
        selected: false,
        disabled: false,
        index: -1,
      },
    },
  };
  private isValidNumberPipe = inject(IsValidNumberPipe);
  private humanizeIntPipe = inject(HumanizeNumberPipe);
  private decimalPipe = inject(DecimalPipe);
  private percentPipe = inject(PercentPipe);
  private titleCasePipe = inject(TitleCasePipe);
  constructor(
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    private httpClient: HttpClient,
    private chartReportService: ChartReportService,
    private cpppService: CpppCalculationService,
    private comparisonHandlerService: ComparisonHandlerService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
  }

  async ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();
    this.isLoading = true;
    this.availableFields = await this.accountService.groupFieldsSub.latest();

    this.accountNumericCustomFields = this.availableFields.metadata.filter(
      (f) => f.fieldType === 'long' || f.fieldType === 'double'
    );

    this.isAnalyticItemEnabled = this.checkCustomNumericFields();

    this.refresh(true, true);
  }

  ngOnDestroy(): void {
    this.groupFieldsSub$?.unsubscribe();
  }

  public async refresh(
    reset?: boolean,
    initialLoading?: boolean,
    refreshedDatasource?: WidgetDataSource
  ): Promise<void> {
    this.isLoading = true;
    this.comparisonFilterCount = this.comparison?.filters.length ?? 0;
    this.multipleFields = !!this.payload.analyticItemTwo;

    this.fieldOne = {
      analyticItem: this.payload.analyticItem,
      analyticItemDisplayName: this.payload.metadataDisplayNameOne,
      previousAnalyticItem: this.payload.analyticItem,
      isVsTotalNumberLoading: true,
      isVsPreviousNumberLoading: true,
      intPrecision: this.payload.intPrecisionOne,
      increaseArrowColor: this.getArrowColor(this.payload.increaseColor),
      decreaseArrowColor: this.getArrowColor(this.payload.decreaseColor),
      showDisplayName: this.payload.showDisplayName,
      displayDataAs: this.payload.displayDataAs,
      reportOptions: {
        scoringStrategy: this.payload?.scoringStrategy,
        boxDirection: this.payload?.boxDirection,
        boxSize: this.payload?.boxSize,
        scoreRange: this.payload?.scoreRange,
      },
      theBigNumberDocumentCount: 0,
      theSmallNumberDocumentCount: 0,
    };
    this.fieldOne.intPrecision =
      this.fieldOne.intPrecision ?? this.calculateIntPrecision(this.fieldOne);

    this.fieldTwo = {
      analyticItem: this.payload.analyticItemTwo,
      analyticItemDisplayName: this.payload.metadataDisplayNameTwo,
      previousAnalyticItem: this.payload.analyticItemTwo,
      isVsTotalNumberLoading: true,
      isVsPreviousNumberLoading: true,
      intPrecision: this.payload.intPrecisionTwo,
      increaseArrowColor: this.getArrowColor(this.payload.increaseColorTwo),
      decreaseArrowColor: this.getArrowColor(this.payload.decreaseColorTwo),
      showDisplayName: this.payload.showDisplayNameTwo,
      displayDataAs: this.payload.displayDataAsTwo,
      reportOptions: {
        scoringStrategy: this.payload?.scoringStrategyTwo,
        boxDirection: this.payload?.boxDirectionTwo,
        boxSize: this.payload?.boxSizeTwo,
        scoreRange: this.payload?.scoreRangeTwo,
      },
      theBigNumberDocumentCount: 0,
      theSmallNumberDocumentCount: 0,
    };
    this.fieldTwo.intPrecision =
      this.fieldTwo.intPrecision ?? this.calculateIntPrecision(this.fieldTwo);

    if (this.dataSourceNotFound || !this.widgetDataSource) {
      this.loadingStarted.emit(initialLoading);
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      return;
    }

    this.loadingStarted.emit(initialLoading);

    if (refreshedDatasource || this.isAnalyticItemEnabled === undefined) {
      this.isAnalyticItemEnabled = this.checkCustomNumericFields();
    }

    const period = this.widgetGroup?.isImpact && {
      first: this.widgetGroup.parameters.currentStart,
      last: this.widgetGroup.parameters.currentEnd,
    };

    //If Comparison, create queryOptions with 1st Comparison filter for big number
    if (this.comparisonFilterCount > 0) {
      this.widgetQueryOptions = this.filterService.getWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        this.comparison.filters[0]
      );
      //If CPPP, create queryOptions with period for big number
    } else {
      this.widgetQueryOptions = this.cpppService.assembleWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        period
      );
    }

    this.setComparisonBucketNames();

    const queryForDocuments = this.filterService.getQueryForDocuments(
      this.widgetQueryOptions,
      false,
      true
    );
    const reportOptions = await this.cpppService.assembleReportOptions(
      this.widget.payload,
      this.widgetDataSource,
      this.widgetGroup
    );

    reportOptions.customFieldSelected = this.widget.payload.customFieldSelected;
    await this.calculateBigNumber(reportOptions, queryForDocuments, reset, this.fieldOne);
    if (this.multipleFields) {
      const reportOptionsTwo = Object.assign({}, reportOptions);
      reportOptionsTwo.analyticItem = this.widget.payload.analyticItemTwo;
      reportOptionsTwo.customFieldSelected = this.widget.payload.metadataTwoCustomFieldSelected;
      await this.calculateBigNumber(reportOptionsTwo, queryForDocuments, reset, this.fieldTwo);
    }

    this.isLoading = false;

    this.loadingFinished.emit(initialLoading);

    return;
  }

  private calculateIntPrecision(field: BigNumberDataDisplay) {
    if (field.analyticItem === 'documents' || field.reportOptions.scoringStrategy === 'SUM') {
      return '0';
    }

    return '1';
  }

  private async calculateBigNumber(
    reportOptions,
    queryForDocuments: string,
    _reset: boolean,
    field: BigNumberDataDisplay
  ) {
    reportOptions.scoringStrategy = field.reportOptions.scoringStrategy;
    reportOptions.boxDirection = field.reportOptions.boxDirection;
    reportOptions.boxSize = field.reportOptions.boxSize;
    reportOptions.scoreRange = field.reportOptions.scoreRange;
    const report = this.chartReportService.configureReport(reportOptions);

    const result: any = await report.executeReport('json', queryForDocuments);
    this.filteredDocumentCount = report.lastDocumentCount;
    field.theBigNumber = result.result;
    field.theBigNumberDocumentCount = result.docs;
    this.displayEmptyState = false;
    if (!result.docs && !result.metadata) {
      field.theBigNumber = NaN;
      this.displayEmptyState = true;
    }
    if (
      !isNaN(field.theBigNumber) &&
      (this.widgetGroup?.isImpact || this.comparisonFilterCount > 1)
    ) {
      //Set initial display type (absolute or percentage) if not previously set in chart options
      if (!field.displayDataAs) {
        const analyticItemType = this.getAnalyticItemType(field.analyticItem);
        analyticItemType === 'rating'
          ? (field.displayDataAs = 'percentage')
          : (field.displayDataAs = 'absolute');
      }

      this.calculateSmallNumber(report, field);
    }
  }

  //Apply comparison filters if comparision, apply date range if CPPP
  private calculateSmallNumber(report, field: BigNumberDataDisplay) {
    let widgetQueryPreviousOptions;
    if (this.comparisonFilterCount > 1) {
      widgetQueryPreviousOptions = this.filterService.getWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        this.comparison.filters[1]
      );
    } else {
      widgetQueryPreviousOptions = this.cpppService.assembleWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        {
          first: this.widgetGroup?.parameters?.previousStart,
          last: this.widgetGroup?.parameters?.previousEnd,
        }
      );
    }

    const queryForPreviousDocuments = this.filterService.getQueryForDocuments(
      widgetQueryPreviousOptions,
      false,
      true
    );

    report
      .executeReport('json', queryForPreviousDocuments)
      .then((res: any) => {
        if (!res.docs) {
          field.vsPreviousNumber = NaN;
        }

        field.isPreviousPeriod0 = !res.result;
        field.theSmallNumber = res.result;
        field.theSmallNumberDocumentCount = res.docs;

        field.displayDataAs === 'absolute'
          ? (field.vsPreviousNumber = field.theBigNumber - res.result)
          : (field.vsPreviousNumber = (field.theBigNumber - res.result) / field.theBigNumber);

        this.filteredDocumentCount += report.lastDocumentCount;
      })
      .finally(() => {
        field.isVsPreviousNumberLoading = false;
      });
  }

  private getArrowColor(reportColor: string) {
    const color = this.bigNumberArrowColors.find((c) => c.id === reportColor);
    if (!color) {
      return { color: 'neutral', id: 'gray' };
    }
    return color;
  }

  private checkCustomNumericFields() {
    if (!this.accountNumericCustomFields || !this.widgetDataSource?.customFields?.length) {
      return false;
    }

    return this.accountNumericCustomFields.some((f) =>
      this.widgetDataSource.customFields.includes(f.fieldName)
    );
  }

  public async exportAsImage(_format: 'blob' | 'canvas' = 'blob', isPpt?: boolean): Promise<Blob> {
    const currentScrollTop = this.widgetContainer.nativeElement.scrollTop;

    return toCanvas(this.widgetContainer.nativeElement, { backgroundColor: 'white' }).then(
      (canvas) => {
        if (!isPpt) {
          canvas = this.createNewCanvasWithTitle(canvas);
        }

        const contentDataURL = canvas.toDataURL('image/svg');

        const body = {
          filename: this.defaultTitle,
          svg: canvas,
        };

        this.widgetContainer.nativeElement.scroll(0, currentScrollTop);

        return firstValueFrom(
          this.httpClient.post<Blob>(contentDataURL, body, {
            responseType: 'blob' as 'json',
          })
        );
      }
    );
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);
    return this.exportToPptx(pptx);
  }

  private exportToPptx(pptx: PptxReport) {
    const dataOptions = {};
    this.addSlideOptions(dataOptions);
    this.createBigNumberAsTable(pptx, dataOptions, this.widgetTitle);
  }

  private createBigNumberAsTable(pptx: PptxReport, dataOptions: any, _title: string) {
    const tableWidth = 12; // in inches
    let colW = [tableWidth];
    if (this.multipleFields) {
      colW = [tableWidth / 2, tableWidth / 2];
    }
    const widthSum = colW.reduce((prev, curr) => prev + curr, 0);
    colW = colW.map((w) => (w / widthSum) * tableWidth);
    pptx.addTableSlide(this.generateRowsForPptExport(), {
      newSlideStartY: 0.5,
      colW,
      w: tableWidth,
      h: 5.5,
      data: dataOptions,
      autoPage: true,
      lineWeight: -0.3,
      valign: 'middle',
    });
  }

  private generateRowsForPptExport() {
    const rows = [];

    if (this.displayEmptyState) {
      const emptyCol = [
        {
          text: translate('widgets.no-documents-found'),
          options: {
            align: 'center',
            fontSize: 32,
            valign: 'middle',
            bold: true,
          },
        },
      ];
      rows.push(emptyCol);
      return rows;
    }

    rows.push([this.buildPptColumn(this.fieldOne)]);
    rows[0].push(this.buildPptColumn(this.fieldTwo));
    return rows;
  }

  private buildPptColumn(field: BigNumberDataDisplay): object {
    const bigNum = field.theBigNumber;
    let text;
    if (this.isValidNumberPipe.transform(bigNum)) {
      text = this.humanizeIntPipe.transform(bigNum, +field.intPrecision, 1000, field.intPrecision);
    }
    const needsToShowComparisonValues = (f: BigNumberDataDisplay) =>
      this.isValidNumberPipe.transform(f.theBigNumber) &&
      (this.hasFilters || this.widgetGroup?.isImpact || this.comparisonFilterCount > 1);
    const col = {
      text: [
        {
          text,
          options: {
            valign: 'middle',
            fontSize: 60,
            align: 'center',
            bold: true,
            ...((field.showDisplayName !== false || needsToShowComparisonValues(field)) && {
              breakLine: true,
            }),
          },
        },
        ...this.getComparisonValues(field),
      ],
    };
    if (field.showDisplayName !== false) {
      col.text.push({
        text: field.analyticItemDisplayName,
        options: {
          align: 'center',
          breakLine: false,
        },
      } as any);
    }
    return col;
  }

  private getComparisonValues(field: BigNumberDataDisplay) {
    if (
      this.isValidNumberPipe.transform(field.theBigNumber) &&
      (this.hasFilters || this.widgetGroup?.isImpact || this.comparisonFilterCount > 1)
    ) {
      const getComparisonValue = (item: BigNumberDataDisplay) => {
        let pipe: DecimalPipe | PercentPipe = this.percentPipe;
        if (item.displayDataAs === 'absolute') {
          pipe = this.decimalPipe;
        }
        return pipe.transform(item.vsPreviousNumber, `1.${item.intPrecision}-${item.intPrecision}`);
      };

      const text =
        field.isPreviousPeriod0 || !this.isValidNumberPipe.transform(field.vsPreviousNumber)
          ? translate('widgets.previous-was-zero')
          : getComparisonValue(field);

      const getXiHexColorForComparisonArrow = (item: BigNumberDataDisplay) => {
        const colorName =
          item.vsPreviousNumber > 0 ? item.increaseArrowColor.color : item.decreaseArrowColor.color;
        const xiColor = XIColorUsage[`color${this.titleCasePipe.transform(colorName)}`];
        if (xiColor) {
          return xiColor.replace('#', '');
        }
        // This is fallback only
        return '000000';
      };

      return [
        {
          text: text,
          options: {
            align: 'center',
            color: getXiHexColorForComparisonArrow(field),
            bullet: {
              code: field.vsPreviousNumber > 0 ? '25B2' : '25BC', //ppt can only work with unicode characters these are up/down arrows
            },
            ...(field.showDisplayName !== false && {
              breakLine: true,
            }),
          },
        },
      ];
    }
    return [];
  }

  public canShowDocumentList(): boolean {
    return false;
  }

  public canShowDataTable(): boolean {
    return false;
  }

  public isAnalyticItemChangeDisabled() {
    return !this.isAnalyticItemEnabled;
  }

  public get defaultTitle(): string {
    const getTitle = (analyticItem) => {
      return analyticItem === 'documents'
        ? translate('widgets.documents')
        : FieldService.getFieldLongName(analyticItem);
    };

    let title = getTitle(this.payload.analyticItem);

    if (this.multipleFields) {
      title = `${title} / ${getTitle(this.payload.analyticItemTwo)}`;
    }
    return title;
  }

  private getAnalyticItemType(analyticItem: string) {
    if (analyticItem === 'documents') {
      return 'documents';
    } else {
      const matchingField = this.availableFields.metadata.find((f) => f.fieldName === analyticItem);
      if (!matchingField) {
        return undefined;
      }

      if (matchingField.metadata.rating === true) {
        return 'rating';
      }
      return matchingField.fieldType as 'documents' | 'long' | 'double';
    }
  }

  public canApplyComparison(comparison?: Comparison): boolean {
    if (!comparison) {
      return true;
    }

    return comparison.filters.length < 3;
  }

  private setComparisonBucketNames(): void {
    if (this.comparison) {
      const sb = new StringBuilder();
      const extractor = this.comparisonHandlerService.filterDisplayValueExtractorFn(
        this.comparison.type
      );

      this.comparison.filters.forEach((fs) => {
        if (sb.length) {
          sb.append(' VS: ');
        }

        sb.append(extractor(fs));
      });

      this.comparisonBucketNames = sb.toString();
    } else {
      this.comparisonBucketNames = undefined;
    }
  }

  override getAiInsightDataPointCount(handler: MatchmakerHandler): number {
    const multiplier = this.multipleFields ? 2 : 1;
    const numbersCount = this.comparisonFilterCount > 1 ? 2 : 1;

    if (this.aiInsightValidatorService.isApplyComparison(handler, this.comparison)) {
      return Math.round((numbersCount * multiplier) / 2);
    }

    return numbersCount * multiplier;
  }

  override getAiInsightEligiblePointCount(): number {
    const documentCount = this.filteredDocumentCount ?? this.widgetDataSource?.documentCount ?? 0;

    if (documentCount < Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT) {
      return 0;
    }

    let eligibleCount = 0;

    // Count field one if valid
    if (!isNaN(this.fieldOne?.theBigNumber)) {
      eligibleCount++;
    }

    // Count field two if valid and multiple fields enabled
    if (this.multipleFields && !isNaN(this.fieldTwo?.theBigNumber)) {
      eligibleCount++;
    }

    return eligibleCount;
  }

  override startAiInsightPointSelectionFlow(handler: MatchmakerHandler) {
    const clickablePoints = this.aiInsightValidatorService.getClickablePoints(handler);
    const pointSelectionRule = this.aiInsightValidatorService.getPointSelectionRule(handler);
    const isApplyComparison = this.aiInsightValidatorService.isApplyComparison(
      handler,
      this.comparison
    );
    const getStrategyForAnalyticItem = (analyticItem, scoring) =>
      analyticItem === 'documents' ? 'COUNT' : scoring?.toLocaleUpperCase();
    let fieldOneDisabled, fieldTwoDisabled;

    if (pointSelectionRule.scoring?.length && pointSelectionRule.scoring[0] !== 'N/A') {
      fieldOneDisabled = !pointSelectionRule.scoring.includes(
        getStrategyForAnalyticItem(
          this.payload.analyticItem,
          this.payload.scoringStrategy ?? 'AVERAGE'
        ) as RatingScoring
      );
      fieldTwoDisabled = !pointSelectionRule.scoring.includes(
        getStrategyForAnalyticItem(
          this.payload.analyticItemTwo,
          this.payload.scoringStrategyTwo ?? 'AVERAGE'
        ) as RatingScoring
      );
    }

    this.insightPointSelectionState = {
      active: true,
      minPoints: clickablePoints.min,
      maxPoints: clickablePoints.max,
      count: 0,
      isApplyComparison,
      fieldOne: {
        disabled: !!fieldOneDisabled,
        theBigNumber: {
          selected: false,
          index: -1,
        },
        theSmallNumber: {
          selected: false,
          disabled: isApplyComparison,
          index: -1,
        },
      },
      fieldTwo: {
        disabled: !!fieldTwoDisabled,
        theBigNumber: {
          selected: false,
          index: -1,
        },
        theSmallNumber: {
          selected: false,
          disabled: isApplyComparison,
          index: -1,
        },
      },
    };
  }

  onInsightPointSelection($event: {
    field: BigNumberDataDisplay;
    fieldState: typeof this.insightPointSelectionState.fieldOne;
    valueName: 'theBigNumber' | 'theSmallNumber';
  }) {
    if (
      $event.fieldState.disabled ||
      $event.fieldState[$event.valueName].disabled ||
      !this.insightPointSelectionState.active ||
      this.insightPointSelectionState.maxPoints === this.insightPointSelectionState.count
    ) {
      return;
    }

    const points = [
      {
        documentCount: $event.field[`${$event.valueName}DocumentCount`],
        y: $event.field[$event.valueName],
        name: $event.field.analyticItem,
        series: {
          index: $event.valueName === 'theBigNumber' ? 0 : 1,
        },
        custom: {
          payloadIndex: $event.field === this.fieldOne ? 0 : 1,
        },
      } as any,
    ];

    if (this.comparisonFilterCount > 1) {
      points[0].series.name = this.comparison.filters[points[0].series.index].label;
    }

    $event.fieldState[$event.valueName].selected = true;
    $event.fieldState[$event.valueName].index = ++this.insightPointSelectionState.count;

    if (this.insightPointSelectionState.isApplyComparison) {
      const otherValueName =
        $event.valueName === 'theBigNumber' ? 'theSmallNumber' : 'theBigNumber';
      const comparisonIndex = otherValueName === 'theBigNumber' ? 0 : 1;

      points[0].series.name = this.comparison.filters[1 - comparisonIndex].label;
      points.push({
        documentCount: $event.field[`${otherValueName}DocumentCount`],
        y: $event.field[otherValueName],
        name: $event.field.analyticItem,
        series: {
          index: comparisonIndex,
          name: this.comparison.filters[comparisonIndex].label,
        },
        custom: {
          payloadIndex: points[0].custom.payloadIndex,
        },
      });

      if (otherValueName === 'theBigNumber') {
        points.reverse();
      }
    }

    this.aiInsightPointSelection.next([
      {
        point: points,
        isSelected: true,
      },
    ]);
  }

  override autoSelectAiInsightPoints(_handler: MatchmakerHandler): void {
    const points = [
      {
        documentCount: this.fieldOne.theBigNumberDocumentCount,
        y: this.fieldOne.theBigNumber,
        name: this.fieldOne.analyticItem,
        series: {
          index: 0,
        } as any,
        custom: {
          payloadIndex: 0,
        },
      },
    ];

    if (this.comparisonFilterCount > 1) {
      points[0].series.name = this.comparison.filters[0].label;
      points.push({
        documentCount: this.fieldOne.theSmallNumberDocumentCount,
        y: this.fieldOne.theSmallNumber,
        name: this.fieldOne.analyticItem,
        series: {
          index: 1,
          name: this.comparison.filters[1].label,
        },
        custom: {
          payloadIndex: 0,
        },
      });
    }
    this.aiInsightPointSelection.next([{ point: points, isSelected: true }]);
  }

  override endAiInsightPointSelectionFlow() {
    this.insightPointSelectionState = {
      active: false,
      minPoints: 0,
      maxPoints: 0,
      count: 0,
      isApplyComparison: false,
      fieldOne: {
        disabled: false,
        theBigNumber: {
          selected: false,
          index: -1,
        },
        theSmallNumber: {
          selected: false,
          disabled: false,
          index: -1,
        },
      },
      fieldTwo: {
        disabled: false,
        theBigNumber: {
          selected: false,
          index: -1,
        },
        theSmallNumber: {
          selected: false,
          disabled: false,
          index: -1,
        },
      },
    };
  }
}
